/**
 * The component inventory from `docs/DESIGN_SYSTEM.md`.
 *
 * Two of these encode product rules rather than presentation, and it is worth saying which:
 *
 *   {@link KindBadge} appears on every row of every list that can hold both kinds. Not as a nicety —
 *   it is the thing that stops a reader scanning a directory and assuming they are looking at
 *   people. It marks kind by SHAPE as well as colour (square for a person, round for an agent), so
 *   it survives a colour-blind reader and a black-and-white print.
 *
 *   {@link KpiCard}'s `dual` variant exists so that a number which CAN be split by kind always is.
 *   A single "4,812 identities" with the agents folded in is the exact habit this product is sold
 *   to break, and the easiest way to prevent it is to make the split the cheaper thing to write.
 */

import type { ReactNode } from "react";
import type { CapabilityTier, IdentityKind, StatusTone } from "@hub/core/browser";

export function Card({ title, link, children, testId }: { title?: string; link?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="card" data-testid={testId}>
      {title && <h3>{title}{link && <span className="link">{link}</span>}</h3>}
      {children}
    </section>
  );
}

export function Pill({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function TierBadge({ tier, meaning }: { tier: CapabilityTier; meaning?: string }) {
  return (
    <span className={`tier t${tier}`}>
      Tier {tier}{meaning ? ` · ${meaning}` : ""}
    </span>
  );
}

export function KindBadge({ kind, label }: { kind: IdentityKind; label?: string }) {
  return (
    <span className={`kind ${kind}`}>
      {label ?? (kind === "human" ? "Person" : "Agent")}
    </span>
  );
}

export function Avatar({ kind, name }: { kind: IdentityKind; name: string }) {
  const initials = name.split(/[\s-]+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  return <div className={`avatar ${kind}`} aria-hidden="true">{initials}</div>;
}

export interface KpiProps {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  /** Two-part bar. `[ok, warn]` as fractions of the whole; the remainder renders as the line. */
  split?: { ok: number; warn?: number };
  /** The human/agent breakdown. Present whenever a number CAN be split by kind. */
  dual?: { human: ReactNode; agent: ReactNode };
}

export function KpiCard({ label, value, delta, split, dual }: KpiProps) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {split && (
        <div className="split" role="presentation">
          <span style={{ width: `${Math.min(100, split.ok * 100)}%`, background: "var(--ok)" }} />
          {split.warn !== undefined && (
            <span style={{ width: `${Math.min(100, split.warn * 100)}%`, background: "var(--warn)" }} />
          )}
        </div>
      )}
      {dual && (
        <div className="dual">
          <div>
            <div className="k"><KindBadge kind="human" label="People" /></div>
            <div className="v">{dual.human}</div>
          </div>
          <div>
            <div className="k"><KindBadge kind="agent" label="Agents" /></div>
            <div className="v">{dual.agent}</div>
          </div>
        </div>
      )}
      {delta && <div className="delta">{delta}</div>}
    </div>
  );
}

/**
 * The band that sits atop Identity and Access: humans on one side, agents on the other, and the
 * principle in the middle. It reads as decoration and is not — it is the claim the whole product
 * makes, placed where a reader cannot get to the data without passing it.
 */
export function ParityStrip({ human, agent, principle }: {
  human: { n: ReactNode; label: string };
  agent: { n: ReactNode; label: string };
  principle: string;
}) {
  return (
    <div className="parity">
      <div className="side">
        <span className="n">{human.n}</span>
        <KindBadge kind="human" label={human.label} />
      </div>
      <span className="principle">{principle}</span>
      <div className="side right">
        <KindBadge kind="agent" label={agent.label} />
        <span className="n">{agent.n}</span>
      </div>
    </div>
  );
}

export function ListItem({ title, detail, children }: { title: ReactNode; detail?: ReactNode; children?: ReactNode }) {
  return (
    <div className="item">
      <div>
        <div className="t">{title}</div>
        {detail && <div className="d">{detail}</div>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export interface ChainStep {
  at: string;
  label: string;
  detail: string;
  hash: string;
  tone?: "ok" | "warn";
}

/**
 * The signed decision chain: request → policy → credential → action → expiry.
 *
 * The hash on each step is shown TRUNCATED and in full on hover, because the value of showing it is
 * that a reader can compare two of them, and twelve characters is enough for that while sixty-four
 * pushes everything else off the row.
 */
export function DecisionChain({ steps, verified }: { steps: ChainStep[]; verified: boolean | null }) {
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        {verified === null
          ? <Pill tone="dim">Not verified in this view</Pill>
          : verified
            ? <Pill tone="ok">Signed · verified</Pill>
            : <Pill tone="bad">Chain verification failed</Pill>}
      </div>
      <div className="chain">
        {steps.length === 0 && <div className="empty">No recorded steps yet.</div>}
        {steps.map((s) => (
          <div key={s.hash + s.at} className={`step ${s.tone === "warn" ? "warn" : ""}`}>
            <div className="ts">{s.at}</div>
            <div className="lb">{s.label}</div>
            <div className="dt">{s.detail}</div>
            <div className="sig" title={s.hash}>✓ {s.hash.slice(0, 12)}…</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      style={{
        width: 40, height: 22, borderRadius: 999, position: "relative", cursor: "pointer",
        border: "1px solid var(--line)", background: on ? "var(--ok)" : "var(--raised)",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 20 : 2, width: 16, height: 16,
        borderRadius: 999, background: on ? "var(--ok-ink)" : "var(--muted)",
      }} />
    </button>
  );
}

/**
 * The banner that says a screen is not looking at a real integration.
 *
 * Rendered wherever data comes from a mock adapter. The alternative — showing convincing numbers
 * with no marking — is how a prospect leaves a demo believing they saw their own estate.
 */
export function NotLiveBanner({ adapters }: { adapters: { port: string; adapter: string }[] }) {
  if (adapters.length === 0) return null;
  return (
    <div className="not-live" role="status">
      <strong>Not connected to a real system.</strong>{" "}
      {adapters.map((a) => `${a.port}: ${a.adapter}`).join(" · ")}. Everything below is generated
      locally and is not your estate.
    </div>
  );
}

export function Toast({ message, kind }: { message: string; kind?: IdentityKind }) {
  return (
    <div className="toast" role="status">
      {kind && <KindBadge kind={kind} label="" />}
      {message}
    </div>
  );
}

/**
 * A number the product is judged on, shown even when it is zero.
 *
 * A KPI that appears only when it is bad teaches its readers that its absence means nothing was
 * measured. `orphanedAgents: 0` is the headline claim of the whole product and must be visible as a
 * zero, every day, or nobody will notice the day it is not.
 */
export function CountPill({ n, goodWhenZero = true }: { n: number; goodWhenZero?: boolean }) {
  const tone: StatusTone = n === 0 ? (goodWhenZero ? "ok" : "dim") : "bad";
  return <Pill tone={tone}>{n}</Pill>;
}

/**
 * The tier-3 coverage gap for one campaign, in `--warn` tone. One rendering for the Assurance card
 * and the agent's Promotion readiness block, so the two cannot word or style the same fact apart.
 * Renders nothing when nothing is missing — a gap line on a compliant campaign would be a claim
 * the data does not make.
 */
export function CampaignGap({ missing, note }: { missing: readonly string[]; note?: string }) {
  if (missing.length === 0) return null;
  return (
    <div data-testid="campaign-gap" style={{ fontSize: 12, color: "var(--warn)", marginTop: 4 }}>
      not covered for tier 3: {missing.join(", ")}{note ? ` — ${note}` : ""}
    </div>
  );
}
